<template>
  <section class="members-data">
    <div class="w-full mt-4 flex flex-wrap items-center justify-between">
      <div class="flex flex-wrap items-center gap-2">
        <Button type="primary" title="Decline" ghost class="rounded shadow flex items-center gap-2">
          Generate spreadsheet
          <PhMicrosoftExcelLogo weight="duotone" :size="16" />
        </Button>
        <Button type="primary" :ghost="creditsLoaded" class="rounded shadow flex items-center gap-2"
          :title="creditsLoaded ? 'Hide the credit columns' : 'Credit columns are heavy to compute, load them on demand'"
          :loading="creditsLoading" @click="toggleCredits">
          {{ creditsLoaded ? 'Hide credit data' : 'Load credit data' }}
          <PhCoins weight="duotone" :size="16" />
        </Button>
      </div>
      <div class>
        <InputSearch class="rounded-md min-w-[320px]" placeholder="Search for email / business name"
          v-model:value="search" clear @change="handleSearch" />
      </div>
    </div>

    <QueryAlert :query="employersQuery" />

    <Modal :visible="creditModal" :footer="null" :destroyOnClose="true" wrap-class-name="empty-modal"
      @ok="creditModal = false" @cancel="creditModal = false">
      <div class="p-4">
        <h2>Subscription cost</h2>
        <Row>
          <Col :span="24">
          <CustumNumberInput name="month" label="Monthly($)" :rules="{
            required: true,
            min: 0,
          }" @setInput="setInput2" :defaultData="month || 0" />
          </Col>
        </Row>
        <Row>
          <Col :span="24">
          <CustumNumberInput name="half_year" label="Half a year($)" :rules="{
            required: true,
            min: 0,
          }" @setInput="setInput2" :defaultData="half_year || 0" />
          </Col>
        </Row>
        <Row>
          <Col :span="24">
          <CustumNumberInput name="year" label="Yearly($)" :rules="{
            required: true,
            min: 0,
          }" :defaultData="year || 0" @setInput="setInput2" />
          </Col>
        </Row>
        <Button type="primary" block class="mt-4" @click="handleEditSubscription">Edit</Button>
      </div>
    </Modal>
    <Modal :visible="creditModal2" :footer="null" :destroyOnClose="true" wrap-class-name="empty-modal"
      @ok="creditModal2 = false" @cancel="creditModal2 = false">
      <div class="p-4">
        <h2>Service fee (percentage of total monthly transportation benefits)</h2>
        <Row>
          <Col :span="24">
          <CustumNumberInput name="fee" label="Fee percentage (%)" :rules="{
            required: true,
            min: 0,
            max: 100,
          }" @setInput="setInput2" :defaultData="fee || 0" />
          </Col>
        </Row>
        <Row>
          <Col :span="12" class="pr-3">
          <Button type="primary" block class="mt-4 btn-error" @click="creditModal2 = false">Close</Button>
          </Col>

          <Col :span="12">
          <Button type="primary" block class="mt-4 pl-3" @click="handleEditServiceFee">Save</Button>
          </Col>
        </Row>
      </div>
    </Modal>

    <div class="mt-2 md:mt-3 lg:mt-4">
      <Table :data-source="tableFormatedData" :columns="visibleColumns"
        :loading="employersQuery && (employersQuery.isFetching || employersQuery.isLoading)" :pagination="false">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <TypographyParagraph ellipsis :content="record.community_name + ', ' + record.name"
              class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'linked_members'">
            <TypographyParagraph ellipsis :content="record.real_count" class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'balance'">
            <TypographyParagraph ellipsis :content="(record.balance ?? 0).toFixed(2)" class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'total_credit'">
            <TypographyParagraph ellipsis :content="(record.total_benefits ?? 0).toFixed(2)" class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'redeemed_credit'">
            <TypographyParagraph ellipsis :content="(-(record.redeemed_credit ?? 0)).toFixed(2)"
              class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'remaining_credit'">
            <TypographyParagraph ellipsis
              :content="(Math.max(0, (record.total_benefits ?? 0) + (record.redeemed_credit ?? 0) - (record.expired_credit ?? 0))).toFixed(2)"
              class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'expiring_credit'">
            <TypographyParagraph ellipsis :content="record.expiring_credit.toFixed(2)"
              class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'expired_credit'">
            <TypographyParagraph ellipsis :content="record.expired_credit.toFixed(2)"
              class="text-text whitespace-nowrap !mb-0" />
          </template>
          <template v-if="column.key === 'joined'">
            <!-- hide Joined on & Status for the totals row -->
            <template v-if="record.key !== 'total'">
              <span v-if="record.status" class="whitespace-nowrap">
                {{ getDate(record.joined) }}, approved
                {{ record.approvedOn ? 'on ' + getDate(record.approvedOn) : '' }}
              </span>
              <span v-else class="w-full flex gap-1 text-yellow-600">
                {{ getDate(record.joined) }}
                <PhHourglassSimpleMedium weight="duotone" size="18" title="pending" />
              </span>
            </template>
            <template v-else>
              <span></span>
            </template>
          </template>

          <template v-if="column.key === 'plan'">
            <span v-if="record.plan" class="whitespace-nowrap text-center capitalize block">{{
              record.plan.replace('_', ' ')
              }}</span>
            <span v-else class="w-full flex justify-center text-yellow-600">N/A</span>
          </template>

          <template v-if="column.key === 'action'">
            <div class="flex items-center gap-2">
              <Popconfirm title="Are you sure you want to edit service fee ?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => handleEdit2(record)">
                <Button type="default" size="small" title="Edit service fee percentage"
                  class="btn-info rounded shadow flex items-center gap-2">
                  <PhReceipt weight="duotone" :size="16" />
                </Button>
              </Popconfirm>
              <!-- Decline  -->
              <Popconfirm title="Are you sure you want to edit subscription ?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => handleEdit(record)">
                <Button type="default" size="small" title="Edit subscription"
                  class="btn-warning rounded shadow flex items-center gap-2">
                  <PhPen weight="duotone" :size="16" />
                </Button>
              </Popconfirm>
              <!-- Decline  -->
              <Popconfirm title="Are you sure you want to decline?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => handleUpdateStatus(record.key, 0)">
                <Button type="primary" size="small" title="Decline"
                  class="btn-warning rounded shadow flex items-center gap-2">
                  <PhX weight="duotone" :size="16" />
                </Button>
              </Popconfirm>

              <!-- Approve  -->
              <Popconfirm title="Are you sure you want to approve?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => handleUpdateStatus(record.key, 1)">
                <Button type="primary" size="small" title="Approve" class="rounded shadow flex items-center gap-2">
                  <PhCheck weight="duotone" :size="16" />
                </Button>
              </Popconfirm>

              <Popconfirm title="Do you want to login with this account?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => signAsEmployer(record?.email, 1)">
                <Button type="primary" size="small" title="Sign as" class="rounded shadow flex items-center gap-2">
                  <PhDoor weight="duotone" :size="16" />
                </Button>
              </Popconfirm>

              <!-- Disable -->
              <Popconfirm title="Are you sure you want to disable?" ok-text="Yes" cancel-text="No"
                :loading="statusLoading" @confirm="() => handleUpdateStatus(record.key, -1)">
                <Button type="primary" size="small" title="Disable"
                  class="btn-error rounded shadow flex items-center gap-2">
                  <PhProhibit weight="duotone" :size="16" />
                </Button>
              </Popconfirm>
            </div>
          </template>
        </template>
      </Table>

      <div v-if="employersData && employersData.meta" class="mt-6 flex items-center justify-center">
        <Pagination :current="employersData?.meta?.current_page" :total="employersData?.meta?.total" show-less-items
          :page-size="employersData?.meta?.per_page" @change="handlePaginationChange" />
      </div>
    </div>

    <!-- query apis  -->
    <EmployersQuery :page="page" :per_page="15" :keyword="keyword" :with_credits="creditsLoaded"
      :key="`${page}-${15}-${keyword}-${creditsLoaded ? 1 : 0}`" />
  </section>
</template>
<script>
/* eslint-disable */
import {
  Table,
  TypographyParagraph,
  Popconfirm,
  Button,
  InputSearch,
  Pagination,
  message,
  Modal,
  Row,
  Col,
} from 'ant-design-vue';
import {
  PhHourglassSimpleMedium,
  PhCheck,
  PhX,
  PhProhibit,
  PhMicrosoftExcelLogo,
  PhDoor,
  PhPen,
  PhReceipt,
  PhCoins,
} from 'phosphor-vue';
import dayjs from 'dayjs';

import EmployersQuery from '@/services/queries/admin/employers/EmployersQuery.vue';

import CustumNumberInput from '@/components/shared/form/CustumNumberInput.vue';
import debounce from 'lodash.debounce';
import handleResErrors from '@/utils/handleResErrors';
import { saveToken } from '@/services/jwt.service';
import employersApi from '../../../services/apis/admin/employers.api';

export default {
  components: {
    Table,
    Button,
    TypographyParagraph,
    Popconfirm,
    PhHourglassSimpleMedium,
    InputSearch,
    EmployersQuery,
    Pagination,
    PhCheck,
    PhDoor,
    Modal,
    CustumNumberInput,
    PhPen,
    PhReceipt,
    Col,
    Row,
    PhX,
    PhProhibit,
    PhMicrosoftExcelLogo,
    PhCoins,
  },
  data() {
    return {
      columns: [
        // {
        //   title: '#',
        //   key: 'key',
        //   dataIndex: 'key',
        // },

        {
          title: 'Business Name',
          key: 'name',
          dataIndex: 'name',
          // sorter: (a, b) => {
          //   if (a.name > b.name) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Email Address',
          dataIndex: 'email',
          key: 'email',
          // sorter: (a, b) => {
          //   if (a.email > b.email) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Subscription Plan',
          dataIndex: 'plan',
          key: 'plan',
          // sorter: (a, b) => {
          //   if (a.plan > b.plan) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Number of members',
          dataIndex: 'members',
          key: 'members',
          // sorter: (a, b) => {
          //   if (a.members > b.members) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Balance',
          dataIndex: 'balance',
          key: 'balance',
          // sorter: (a, b) => {
          //   if (a.members > b.members) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Total credit',
          dataIndex: 'total_credit',
          key: 'total_credit',
          // sorter: (a, b) => {
          //   if (a.members > b.members) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Redeemed credit',
          dataIndex: 'redeemed_credit',
          key: 'redeemed_credit',
          // sorter: (a, b) => {
          //   if (a.members > b.members) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Remaining credit',
          dataIndex: 'remaining_credit',
          key: 'remaining_credit',
          // sorter: (a, b) => {
          //   if (a.members > b.members) {
          //     return 1;
          //   }
          //   return -1;
          // },
        },
        {
          title: 'Expiring credit',
          dataIndex: 'expiring_credit',
          key: 'expiring_credit',
        },
        {
          title: 'Expired credit',
          dataIndex: 'expired_credit',
          key: 'expired_credit',
        },
        {
          title: 'Joined on & Status',
          dataIndex: 'joined',
          key: 'joined',
          // sorter: (a, b) => {
          //   const aDate = new Date(a.joined).getTime();
          //   const bDate = new Date(b.joined).getTime();
          //   return bDate - aDate;
          // },
        },

        {
          title: '',
          key: 'action',
          fixed: 'right',
        },
      ],

      tableData: [
        {
          key: '1',
          name: 'Daniel Martin',
          email: 'danielmartin@gmail.com',
          joined: 1664882267,
          plan: 'free',
          members: 30,
        },

        {
          key: '2',
          name: 'Sara Doe',
          email: 'Sara@gmail.com',
          joined: 1601810267,
          plan: 'premium',
          members: 30,
        },
        // 1664882267 1601810267
        ...Array(50)
          .fill('dd')
          .map((_, ix) => ({
            key: ix + 3,
            name: `John ${ix + 3}`,
            email: `johndoe${ix + 3}@gmail.com`,
            joined: this.getRandomInt(1420063200000, 1681478087456),
            plan: 'premium',
            members: this.getRandomInt(10, 500),
          })),
      ],
      // the credit columns force the API to aggregate every employer's wallet
      // transactions, so they stay hidden until asked for
      creditsLoaded: false,
      creditColumnKeys: [
        'total_credit',
        'redeemed_credit',
        'remaining_credit',
        'expiring_credit',
        'expired_credit',
      ],
      search: '',
      searchData: [],
      page: 1,
      pageSize: 15,
      discount: 0,
      total_price: 0,
      userId: null,
      name: '',
      email: '',
      modalMemb: null,
      delLoading: false,
      keyword: '',
      creditModal: false,
      creditModal2: false,
      statusLoading: false,
      month: 0,
      half_year: 0,
      year: 0,
      fee: 0,
      excludeEmails: [
        "admin@commutrics.com",
        "admin@commutrics.edu",
        "ccd@denvergov.com",
        "admin03@commutrics.com",
        "admin@commutemetrics.com",
        "admin@commutrics.com",
        "expiredcredits@expiredcredits.com"
      ], // <-- added empty array of emails to exclude in totals
    };
  },
  computed: {
    employersQuery() {
      return this.$store.state.adminEmployers.employersQuery;
    },
    visibleColumns() {
      if (this.creditsLoaded) return this.columns;
      return this.columns.filter((c) => !this.creditColumnKeys.includes(c.key));
    },
    creditsLoading() {
      if (!this.creditsLoaded) return false;
      return Boolean(
        this.employersQuery && (this.employersQuery.isFetching || this.employersQuery.isLoading),
      );
    },
    employersData() {
      return this.$store.getters.employersData;
    },
    tableFormatedData() {
      if (!this.employersData || !this.employersData.data) return [];

      const data = this.employersData.data.map((d, ix) => ({
        // eslint-disable-next-line no-underscore-dangle
        key: d._id || d.id || ix,
        userId: d?._id,
        name: d.name,
        email: d.email,
        joined: d.created_at,
        status: d.status,
        approvedOn: d.approved_on || null,
        plan: d.subscription_plan,
        members: Number(d.employees_count) || 0,
        community_name: d.community_name,
        activeSubscription: d?.active_subscription ? 1 : 0,
        discount: d?.active_subscription ? d?.active_subscription?.discount : 0,
        total_price: d?.active_subscription ? d?.active_subscription?.total_price : 0,
        real_count: d?.real_count,
        redeemed_credit: Number(Number(d?.redeemed || 0).toFixed(2)),
        total_benefits: Number(Number(d?.total_benefits || 0).toFixed(2)),
        month: d?.custom_subscription_plans?.month,
        half_year: d?.custom_subscription_plans?.half_year,
        year: d?.custom_subscription_plans?.year,
        fee: d?.service_fee?.fee ?? 0,
        expiring_credit: Number(Number(d?.amount_gonna_expire || 0).toFixed(2)),
        expired_credit: Number(Number(d?.expired_credit || 0).toFixed(2)),
        balance: Number(Number(d?.wallet?.balance || 0).toFixed(2)),
      }));

      // compute totals excluding any emails in excludeEmails
      const filtered = data.filter((r) => !this.excludeEmails.includes(r.email));

      const totals = {
        key: 'total',
        userId: null,
        name: 'Total',
        email: '',
        joined: '',
        status: true,
        approvedOn: null,
        plan: '',
        members: filtered.reduce((s, r) => s + (Number(r.members) || 0), 0),
        community_name: '',
        activeSubscription: 0,
        discount: 0,
        total_price: 0,
        real_count: '',
        // keep redeemed and totals positive; templates will render as needed
        redeemed_credit: Number(filtered.reduce((s, r) => s + (Number(r.redeemed_credit) || 0), 0).toFixed(2)),
        total_benefits: Number(filtered.reduce((s, r) => s + (Number(r.total_benefits) || 0), 0).toFixed(2)),
        expiring_credit: Number(filtered.reduce((s, r) => s + (Number(r.expiring_credit) || 0), 0).toFixed(2)),
        expired_credit: Number(filtered.reduce((s, r) => s + (Number(r.expired_credit) || 0), 0).toFixed(2)),
        // remaining = total_benefits + redeemed_credit - expired_credit (clamped to >=0)
        remaining_credit: (() => {
          const tb = filtered.reduce((s, r) => s + (Number(r.total_benefits) || 0), 0);
          const rc = filtered.reduce((s, r) => s + (Number(r.redeemed_credit) || 0), 0);
          const ec = filtered.reduce((s, r) => s + (Number(r.expired_credit) || 0), 0);
          const val = tb + rc - ec;
          return Number((val < 0 ? 0 : val).toFixed(2));
        })(),
        balance: Number(filtered.reduce((s, r) => s + (Number(r.balance) || 0), 0).toFixed(2)),
      };

      // append totals row so it shows at the bottom of the table
      return [...data, totals];
    },
  },
  methods: {
    toggleCredits() {
      if (this.creditsLoading) return;
      this.creditsLoaded = !this.creditsLoaded;
    },
    handlePaginationChange(page, pageSize) {
      // , filters, sorter, data
      this.page = page;
      this.pageSize = pageSize;
    },
    getRandomInt(min, max) {
      const xmin = Math.ceil(min);
      const xmax = Math.floor(max);
      return Math.floor(Math.random() * (xmax - xmin + 1)) + min;
    },
    getDate(d) {
      // February 18, 2022, 4:43 am
      return dayjs(d).format('MMM DD, YYYY');
    },
    handleSearch(e) {
      this.debounceSearch(e.target.value, this);
    },
    debounceSearch: debounce((search, app) => {
      // eslint-disable-next-line no-param-reassign
      app.keyword = search;
      // eslint-disable-next-line no-param-reassign
      app.page = 1;
      // eslint-disable-next-line no-param-reassign
      app.pageSize = 15;
    }, 400),
    async handleUpdateStatus(id, status) {
      this.statusLoading = true;
      try {
        const res = await employersApi.updateEmployer({ id, status });
        if (res) {
          message.success('Status updated successfully');
        }
        if (this.employersQuery) {
          this.employersQuery.refetch();
        }
      } catch (error) {
        message.error(handleResErrors(error), 10);
      } finally {
        this.statusLoading = false;
      }
    },
    setInput2(val) {
      if (val.type && val.type.value) {
        this.type = val.type.value;
      }
      if (val.month) {
        this.month = val.month.value;
      }
      if (val.half_year) {
        this.half_year = val.half_year.value;
      }
      if (val.year) {
        this.year = val.year.value;
      }
      if (val.fee) {
        this.fee = val.fee.value;
      }
    },
    handleEdit(record) {
      this.discount = record?.discount;
      this.total_price = record?.total_price;
      this.userId = record?.userId;
      this.month = record?.month;
      this.half_year = record?.half_year;
      this.year = record?.year;
      this.creditModal = 1;
    },
    handleEdit2(record) {
      this.fee = record?.fee ?? 0;
      this.userId = record?.userId;
      this.creditModal2 = 1;
    },
    async handleEditSubscription() {
      try {
        let month = this.month;
        let half_year = this.half_year;
        let year = this.year;
        let id = this.userId;
        const res = await employersApi.editEmployerSubscription({ month, half_year, year, id });
        if (res) {
          message.success('updated successfully');
        }
      } catch (error) {
        message.error(handleResErrors(error), 10);
      }
    },
    async handleEditServiceFee() {
      try {
        let fee = this.fee;
        let id = this.userId;
        const res = await employersApi.editEmployerServiceFee({ fee, id });
        if (res) {
          message.success('updated successfully');
          this.creditModal2 = false;
          if (this.employersQuery) {
            this.employersQuery.refetch();
          }
        }
      } catch (error) {
        message.error(handleResErrors(error), 10);
      }
    },
    async signAsEmployer(email) {
      try {
        const res = await employersApi.signAsEmployer({ email });
        if (res.status) {
          if (res.is_active) {
            message.success('logged in successfully');
            localStorage.setItem('is_signas', true);
            saveToken(res.accessToken);
            this.$router.push('/dashboard');
          }
        }
      } catch (error) {
        message.error(handleResErrors(error), 10);
      }
    },
  },
};
</script>
